/**
 * Kesehatan env Supabase — helper MURNI (tanpa `process.env`, tanpa jaringan).
 *
 * Lahir dari bug live pasca-**migrasi region DB** (2026-08-08, project baru
 * `MitraHubSpace PROD (Singapore)`): tombol **Ganti Password** & **Reset
 * Password** di halaman edit pelanggan menjawab **"Invalid API key"**,
 * sementara SELURUH sisa aplikasi (login, katalog, board, nota) normal.
 *
 * Asimetri itu bukan kebetulan, dan itulah petunjuk yang menunjuk akarnya:
 * jalur anon (`NEXT_PUBLIC_SUPABASE_URL`/`_ANON_KEY`) dibaca lewat
 * `sanitizeEnv` di `supabase/config.ts` — yang membuang karakter tak terlihat
 * hasil salin-tempel ke panel Hostinger (spasi tepi, newline, zero-width
 * space, NBSP) — sedangkan jalur **service role** membaca `process.env`
 * MENTAH di dua tempat (`supabase/service.ts`, `supabase/storeContextServer.ts`).
 * Satu newline di ujung key sudah cukup: anon selamat karena dibersihkan,
 * service_role tidak → gerbang API memulangkan 401 "Invalid API key", dan
 * satu-satunya fitur yang MEWAJIBKAN service role di layar admin sehari-hari
 * adalah `auth.admin.updateUserById` alias ganti/reset password.
 *
 * Berkas ini menampung dua hal yang bisa dikunci tes tanpa menyentuh Supabase:
 *   1. `sanitizeSupabaseEnv` — pembersih yang SAMA untuk semua key.
 *   2. Pemeriksaan yang MUSTAHIL salah-positif: key milik project LAIN, atau
 *      key ber-role bukan `service_role`. Keduanya dibaca dari klaim di dalam
 *      key legacy (JWT) itu sendiri, jadi kesimpulannya deterministik — bukan
 *      tebakan. Kalau salah satunya benar, panggilan PASTI 401; menolak lebih
 *      awal dengan kalimat yang menyebut akarnya jauh lebih berguna daripada
 *      meneruskan "Invalid API key" mentah ke muka admin.
 *
 * Key format BARU (`sb_secret_…`/`sb_publishable_…`) sengaja OPAQUE — tak
 * membawa klaim apa pun. Untuk key itu pemeriksaan project ref tak bisa
 * dilakukan dan fungsi di sini memulangkan `null` (= "tidak ada masalah yang
 * bisa saya buktikan"), BUKAN menuduh. Satu-satunya yang tetap terdeteksi:
 * awalan `sb_publishable_` yang jelas-jelas bukan kunci rahasia.
 */

/**
 * Buang karakter non-ASCII-printable dari nilai env.
 *
 * Cermin `sanitizeEnv` di `supabase/config.ts` (sengaja diduplikasi di modul
 * murni ini supaya bisa diuji; `config.ts` kini memanggil yang INI agar hanya
 * ada satu definisi yang hidup). URL & key Supabase yang benar SELALU ASCII,
 * jadi pembersihan ini tak pernah mengubah nilai yang sudah valid.
 */
export function sanitizeSupabaseEnv(value?: string | null): string | undefined {
  if (!value) return undefined;
  const cleaned = value.replace(/[^\x21-\x7E]/g, "");
  return cleaned.length > 0 ? cleaned : undefined;
}

/** Project ref dari URL Supabase (`https://<ref>.supabase.co`). */
export function projectRefFromUrl(url?: string | null): string | null {
  const clean = sanitizeSupabaseEnv(url);
  if (!clean) return null;
  // Host kustom / self-host tak memakai pola ini → null (tak bisa dibandingkan).
  const match = /^https?:\/\/([a-z0-9-]+)\.supabase\.(co|in|red)(?::\d+)?(?:\/|$)/i.exec(
    clean
  );
  return match ? match[1].toLowerCase() : null;
}

type LegacyClaims = { ref?: unknown; role?: unknown };

/**
 * Klaim di dalam key legacy (JWT `HS256` ber-payload `{ref, role}`).
 * Key format baru / string sembarang → `null`.
 */
function legacyKeyClaims(key: string): LegacyClaims | null {
  const parts = key.split(".");
  if (parts.length !== 3) return null;
  try {
    const b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = b64 + "=".repeat((4 - (b64.length % 4)) % 4);
    const parsed: unknown = JSON.parse(atob(padded));
    if (!parsed || typeof parsed !== "object") return null;
    return parsed as LegacyClaims;
  } catch {
    return null;
  }
}

/** Project ref yang tertulis DI DALAM key legacy; `null` bila tak terbaca. */
export function projectRefFromKey(key?: string | null): string | null {
  const clean = sanitizeSupabaseEnv(key);
  if (!clean) return null;
  const ref = legacyKeyClaims(clean)?.ref;
  return typeof ref === "string" && ref ? ref.toLowerCase() : null;
}

/** Role yang tertulis DI DALAM key legacy (`anon`/`service_role`). */
export function roleFromKey(key?: string | null): string | null {
  const clean = sanitizeSupabaseEnv(key);
  if (!clean) return null;
  if (clean.startsWith("sb_publishable_")) return "anon";
  if (clean.startsWith("sb_secret_")) return "service_role";
  const role = legacyKeyClaims(clean)?.role;
  return typeof role === "string" && role ? role : null;
}

export type ServiceKeyProblemCode = "missing" | "wrong_role" | "project_mismatch";

export type ServiceKeyProblem = {
  code: ServiceKeyProblemCode;
  /** Kalimat siap tampil ke admin — menyebut akar + langkah perbaikannya. */
  message: string;
};

/**
 * Masalah pada `SUPABASE_SERVICE_ROLE_KEY` yang bisa DIBUKTIKAN dari nilainya
 * sendiri; `null` = tak ada yang bisa dibuktikan salah (belum tentu benar —
 * key bisa saja sudah dicabut/di-rotate, dan itu hanya ketahuan saat dipakai).
 *
 * Sengaja TIDAK pernah menebak: hanya tiga keadaan yang mustahil bekerja yang
 * dilaporkan, sehingga aman dipakai sebagai gerbang fail-fast di route.
 */
export function serviceKeyProblem(
  url?: string | null,
  key?: string | null
): ServiceKeyProblem | null {
  const cleanKey = sanitizeSupabaseEnv(key);
  if (!cleanKey) {
    return {
      code: "missing",
      message:
        "SUPABASE_SERVICE_ROLE_KEY belum diisi di server. Isi dari Supabase " +
        "Dashboard → Project Settings → API Keys (kunci service_role/secret), " +
        "lalu restart aplikasi.",
    };
  }

  const role = roleFromKey(cleanKey);
  if (role && role !== "service_role") {
    return {
      code: "wrong_role",
      message:
        `SUPABASE_SERVICE_ROLE_KEY berisi kunci ber-role "${role}", bukan ` +
        "service_role. Kunci anon/publishable tidak boleh dipakai di sini — " +
        "salin kunci service_role (secret) dari Supabase Dashboard → " +
        "Project Settings → API Keys, lalu restart aplikasi.",
    };
  }

  const urlRef = projectRefFromUrl(url);
  const keyRef = projectRefFromKey(cleanKey);
  if (urlRef && keyRef && urlRef !== keyRef) {
    return {
      code: "project_mismatch",
      message:
        `SUPABASE_SERVICE_ROLE_KEY milik project "${keyRef}" sedangkan ` +
        `NEXT_PUBLIC_SUPABASE_URL menunjuk project "${urlRef}". Setelah ` +
        "pindah project/region, service role key WAJIB diambil ulang dari " +
        "project yang baru (Dashboard → Project Settings → API Keys), lalu " +
        "aplikasi di-restart.",
    };
  }

  return null;
}

/**
 * Terjemahkan kegagalan otentikasi kunci dari Supabase menjadi kalimat yang
 * menyebut apa yang harus diperbaiki.
 *
 * Dipakai SESUDAH panggilan gagal: `serviceKeyProblem` hanya menangkap
 * kesalahan yang terbaca dari bentuk key, sedangkan key yang bentuknya benar
 * tapi sudah di-rotate/dicabut baru ketahuan di sini. Selain 401/"Invalid API
 * key", pesan asli diteruskan apa adanya — jangan menimpa error yang sudah
 * jelas (mis. "Password should be at least 6 characters") dengan tebakan env.
 */
export function describeServiceKeyFailure(input: {
  message?: string | null;
  status?: number | null;
  problem?: ServiceKeyProblem | null;
}): string | null {
  const raw = (input.message ?? "").trim();
  const looksLikeKeyFailure =
    input.status === 401 || /invalid api key|no api key/i.test(raw);
  if (!looksLikeKeyFailure) return null;
  if (input.problem) return input.problem.message;
  return (
    "Supabase menolak kunci service role (Invalid API key). Biasanya karena " +
    "SUPABASE_SERVICE_ROLE_KEY di server masih milik project lama atau sudah " +
    "di-rotate. Ambil ulang kunci service_role dari Supabase Dashboard → " +
    "Project Settings → API Keys, perbarui env, lalu restart aplikasi."
  );
}

/**
 * Kalimat bawaan saat lookup registry toko gagal TANPA sebab yang bisa
 * ditunjuk — satu-satunya keadaan yang memang layak disebut "sementara".
 */
export const STORE_REGISTRY_UNAVAILABLE_MESSAGE =
  "Registry toko sedang tidak terbaca. Coba lagi sebentar lagi.";

/**
 * Pesan untuk gerbang fail-closed yang gagal karena domain toko tak
 * ter-resolusi (`dummyDomainForStore` → null).
 *
 * Lahir dari bug live 2026-08-09: Import Pelanggan menjawab "Registry toko
 * sedang tidak terbaca. Coba lagi sebentar lagi." padahal registrinya sehat —
 * yang ditolak adalah KUNCI service role (masih milik project lama pasca
 * migrasi region). Lookup toko by-id berjalan dengan service role, dan
 * kegagalannya ditelan jadi `null` yang tak bisa dibedakan dari "toko tak ada".
 * Akibatnya kalimat "coba lagi sebentar lagi" mengirim admin ke perulangan
 * tanpa ujung: penyebabnya permanen sampai env diperbaiki.
 *
 * Urutannya sengaja: 401 → sebut kunci; masalah kunci yang TERBUKTI dari
 * bentuknya (salah project/role/kosong) → sebut itu apa pun status HTTP-nya,
 * karena kunci begitu memang menggagalkan SEMUA panggilan service role; selain
 * itu barulah "coba lagi sebentar lagi" yang memang transien.
 */
export function describeStoreRegistryFailure(input: {
  status?: number | null;
  problem?: ServiceKeyProblem | null;
}): string {
  const keyFailure = describeServiceKeyFailure({
    status: input.status ?? null,
    problem: input.problem ?? null,
  });
  if (keyFailure) return keyFailure;
  if (input.problem) return input.problem.message;
  return STORE_REGISTRY_UNAVAILABLE_MESSAGE;
}
